import { useMemo } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { InfoHint } from "@/components/ui/info-hint";
import { KpiBasisLabel, KpiBasisNote } from "@/components/reports/KpiBasisLabel";
import { useCommissionKpis } from "@/hooks/useInvoices";
import { useGoals, useUpsertPersonalGoal, useUpsertTeamGoal } from "@/hooks/useGoals";
import { GoalEditDialog } from "@/components/goals/GoalEditDialog";
import { SectionLoadError } from "@/components/invoicing/SectionErrorBoundary";
import { useQueryClient } from "@tanstack/react-query";
import { formatKpiCurrency } from "@/lib/formats/currency";
import { computeForecast } from "@/lib/goalForecast";

interface PaidCommissionCardProps {
  tramsAccountId?: number;
  viewMode: "my" | "all";
  returnDateFrom?: string;
  returnDateTo?: string;
}

export function PaidCommissionCard({
  tramsAccountId,
  viewMode,
  returnDateFrom,
  returnDateTo,
}: PaidCommissionCardProps) {
  const queryClient = useQueryClient();
  const currentYear = new Date().getFullYear();

  const {
    data: kpiData,
    isLoading: kpiLoading,
    error: kpiError,
  } = useCommissionKpis({ tramsAccountId, viewMode, returnDateFrom, returnDateTo });

  const {
    data: goalsData,
    isLoading: goalsLoading,
    error: goalsError,
  } = useGoals(currentYear);

  const upsertPersonal = useUpsertPersonalGoal();
  const upsertTeam = useUpsertTeamGoal();

  const isLoading = kpiLoading || goalsLoading;
  const error = kpiError || goalsError;

  const hasTeamAccess = goalsData?.showTeamByDefault ?? false;
  const canEditTeamGoals = goalsData?.canEditTeamGoals ?? false;

  const goalScope: "team" | "personal" =
    viewMode === "all" && (hasTeamAccess || canEditTeamGoals)
      ? "team"
      : "personal";

  const activeGoal = goalScope === "team" ? goalsData?.team : goalsData?.personal;
  const goalAmount = parseFloat(activeGoal?.commissionGoal ?? "0");
  const paidAmount = parseFloat(kpiData?.paidYtd ?? "0");
  const outstandingAmount = parseFloat(kpiData?.outstandingYtd ?? "0");

  /** Trip return-date filter on Reports: KPI window is not full calendar YTD — skip extrapolation */
  const extrapolateToYearEnd = !(
    (returnDateFrom && returnDateFrom.trim()) || (returnDateTo && returnDateTo.trim())
  );

  const forecast = useMemo(
    () =>
      computeForecast(paidAmount, outstandingAmount, goalAmount, extrapolateToYearEnd),
    [paidAmount, outstandingAmount, goalAmount, extrapolateToYearEnd],
  );

  const canEdit =
    goalScope === "personal" ||
    (goalScope === "team" && canEditTeamGoals);

  if (error) {
    return (
      <SectionLoadError
        sectionName="YTD GEM Qualifying Commission"
        error={error}
        onRetry={() => {
          queryClient.invalidateQueries({ queryKey: ["/api/invoices/commission-kpis"] });
          queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
        }}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="rounded-xl border bg-card p-5">
        <Skeleton className="h-4 w-36 mb-3" />
        <Skeleton className="h-10 w-44 mb-1" />
        <Skeleton className="h-3 w-28 mb-3" />
        <Skeleton className="h-6 w-full rounded-lg" />
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-card overflow-hidden flex flex-col">
      {/* Main metric area */}
      <div className="p-5 pb-4">
        <div className="flex items-center gap-1.5 mb-1">
          <p className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
            YTD GEM Qualifying Commission
          </p>
          <InfoHint label="How YTD GEM qualifying commission is calculated">
            <p>
              Gross commission from closed invoices that counts toward GEM
              qualification this year. Only closed invoices qualify. Gross
              means the full commission before the agency split.
            </p>
            <KpiBasisNote card="ytdGemQualificationCommission" />
          </InfoHint>
        </div>
        <KpiBasisLabel card="ytdGemQualificationCommission" className="mb-1" />
        <p className="text-4xl font-bold tracking-tight text-foreground">
          {formatKpiCurrency(paidAmount)}
        </p>

        {/* Goal display */}
        {goalAmount > 0 && (
          <p className="text-lg text-muted-foreground mt-0.5">
            /{formatKpiCurrency(goalAmount)}
          </p>
        )}
      </div>

      {goalAmount > 0 ? (
        <div className="bg-muted/40 px-5 py-3.5 flex items-start justify-between gap-2 mt-auto border-t border-border/50">
          <div className="min-w-0 flex-1">
            <p
              className={`text-sm font-semibold ${
                forecast.onTrack
                  ? "text-green-700 dark:text-green-400"
                  : "text-red-600 dark:text-red-400"
              }`}
            >
              {forecast.onTrack ? "On Track" : "Off Track"}
            </p>
            <p className="text-sm text-muted-foreground break-words">
              {forecast.label}
            </p>
          </div>
          {canEdit && (
            <GoalEditDialog
              title={goalScope === "team" ? "Team Goals" : "Personal Goals"}
              description={`Set ${goalScope} targets for ${currentYear}.`}
              goal={activeGoal ?? null}
              year={currentYear}
              onSave={async (p) => {
                if (goalScope === "team") {
                  await upsertTeam.mutateAsync(p);
                } else {
                  await upsertPersonal.mutateAsync(p);
                }
              }}
              isSaving={upsertPersonal.isPending || upsertTeam.isPending}
            />
          )}
        </div>
      ) : (
        <div className="bg-muted/40 px-5 py-3.5 flex items-center justify-between gap-2 mt-auto border-t border-border/50">
          <p className="text-sm text-muted-foreground">
            No commission goal set
          </p>
          {canEdit && (
            <GoalEditDialog
              title={goalScope === "team" ? "Team Goals" : "Personal Goals"}
              description={`Set ${goalScope} targets for ${currentYear}.`}
              goal={activeGoal ?? null}
              year={currentYear}
              onSave={async (p) => {
                if (goalScope === "team") {
                  await upsertTeam.mutateAsync(p);
                } else {
                  await upsertPersonal.mutateAsync(p);
                }
              }}
              isSaving={upsertPersonal.isPending || upsertTeam.isPending}
            />
          )}
        </div>
      )}
    </div>
  );
}
